import type { ChatMessage } from '@/types/chat';
import { draftChat } from '@/constants';

export const getChatKey = (chatId?: string) => chatId || draftChat;

interface CacheListenerPayload {
  chatKey?: string;
  type: 'messages' | 'loading' | 'error';
}

type CacheListener = (payload: CacheListenerPayload) => void;

const messagesCacheByChatKey: Record<string, ChatMessage[]> = {};
const isLoadingCacheByChatKey: Record<string, boolean> = {};
const abortControllerByChatKey: Record<string, AbortController | undefined> = {};
const errorCache: { value: string | null } = { value: null };

const listeners = new Set<CacheListener>();

const notify = (payload: CacheListenerPayload) => {
  listeners.forEach((listener) => {
    try {
      listener(payload);
    } catch (e) {
      // Listener errors must never break streaming.
      console.warn('chatMessagesCache listener failed', e);
    }
  });
};

export const chatMessagesCache = {
  subscribe(listener: CacheListener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  getMessages(chatId?: string) {
    return messagesCacheByChatKey[getChatKey(chatId)] ?? [];
  },

  setMessages(chatId: string | undefined, next: ChatMessage[]) {
    const key = getChatKey(chatId);
    messagesCacheByChatKey[key] = next;
    notify({ chatKey: key, type: 'messages' });
  },

  updateMessages(chatId: string | undefined, updater: (prev: ChatMessage[]) => ChatMessage[]) {
    const key = getChatKey(chatId);
    const prev = messagesCacheByChatKey[key] ?? [];
    const next = updater(prev);
    if (next === prev) return;
    messagesCacheByChatKey[key] = next;
    notify({ chatKey: key, type: 'messages' });
  },

  isLoading(chatId?: string) {
    return Boolean(isLoadingCacheByChatKey[getChatKey(chatId)]);
  },

  setLoading(chatId: string | undefined, value: boolean) {
    const key = getChatKey(chatId);
    if (value) {
      isLoadingCacheByChatKey[key] = true;
    } else {
      delete isLoadingCacheByChatKey[key];
    }
    notify({ chatKey: key, type: 'loading' });
  },

  getError() {
    return errorCache.value;
  },

  setError(value: string | null) {
    errorCache.value = value;
    notify({ type: 'error' });
  },

  setAbortController(chatId: string | undefined, controller: AbortController | undefined) {
    const key = getChatKey(chatId);
    if (controller) {
      abortControllerByChatKey[key] = controller;
    } else {
      delete abortControllerByChatKey[key];
    }
  },

  abort(chatId?: string) {
    const key = getChatKey(chatId);
    const controller = abortControllerByChatKey[key];

    if (controller) {
      controller.abort();
      delete abortControllerByChatKey[key];
      return;
    }

    // Stream may have been started with overrideChatId while the hook still keys on draft.
    for (const activeKey of Object.keys(abortControllerByChatKey)) {
      abortControllerByChatKey[activeKey]?.abort();
      delete abortControllerByChatKey[activeKey];
    }
  },

  /**
   * Move all cached state keyed by `fromChatId` to `toChatId`.
   * Used when a "draft chat" (fallback key) becomes a persisted chatId.
   */
  migrate(fromChatId: string | undefined, toChatId: string) {
    const fromKey = getChatKey(fromChatId);
    const toKey = getChatKey(toChatId);
    if (!fromKey || !toKey || fromKey === toKey) return;

    const messages = messagesCacheByChatKey[fromKey];
    if (messages) {
      messagesCacheByChatKey[toKey] = messages;
      delete messagesCacheByChatKey[fromKey];
      notify({ chatKey: toKey, type: 'messages' });
    }

    if (isLoadingCacheByChatKey[fromKey]) {
      isLoadingCacheByChatKey[toKey] = true;
      delete isLoadingCacheByChatKey[fromKey];
      notify({ chatKey: toKey, type: 'loading' });
    }

    const controller = abortControllerByChatKey[fromKey];
    if (controller) {
      abortControllerByChatKey[toKey] = controller;
      delete abortControllerByChatKey[fromKey];
    }
  },
};
